import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = '' }: HeroSectionProps) => {
  const distressSituations = [
    {
      icon: 'HomeIcon',
      title: 'Avoid Foreclosure',
      description: 'Stop the foreclosure process and protect your credit',
    },
    {
      icon: 'HeartIcon',
      title: 'Divorce Settlement',
      description: 'Quick property division without lengthy market delays',
    },
    {
      icon: 'DocumentTextIcon',
      title: 'Inherited Property',
      description: 'Turn inherited burden into immediate cash relief',
    },
    {
      icon: 'CurrencyDollarIcon',
      title: 'Financial Relief',
      description: 'Access equity fast when you need it most',
    },
  ];

  return (
    <section className={`relative bg-background py-20 lg:py-28 ${className}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16 items-center">
          {/* Content */}
          <div>
            <div className="mb-16 h-px w-20 bg-accent"></div>
            <div className="mb-7 font-heading text-xl italic text-primary">— For Sellers</div>

            <h1 className="max-w-[720px] text-[52px] md:text-[70px] lg:text-[82px] font-normal leading-[0.95] text-foreground mb-7">
              A discreet answer
              <br />
              for your <span className="italic text-plum">property.</span>
            </h1>

            <p className="max-w-[610px] font-heading text-[22px] leading-[1.55] text-muted-foreground mb-12">
              Whether you&apos;re navigating foreclosure, divorce, inheritance, or financial stress,
              we provide a fair cash solution with privacy and pace.
            </p>

            <div className="mb-10 flex items-center gap-4 text-accent">
              <span className="h-px w-10 bg-accent"></span>
              <span>✦</span>
              <span className="h-px w-10 bg-accent"></span>
            </div>

            {/* Distress Situations */}
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {distressSituations.map((situation, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 border border-border bg-card/70 p-4 transition-colors hover:border-plum"
                >
                  <div className="flex-shrink-0 w-9 h-9 bg-plum-tint border border-plum/15 flex items-center justify-center">
                    <Icon name={situation.icon as any} size={18} className="text-plum" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[14px] text-foreground mb-0.5">
                      {situation.title}
                    </h3>
                    <p className="text-[12px] text-muted-foreground leading-snug">
                      {situation.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-7">
              <Link
                href="#property-form"
                className="inline-flex items-center justify-center gap-2 bg-plum px-6 py-3 text-[12px] font-bold uppercase tracking-[0.24em] text-primary-foreground transition-colors hover:bg-plum-deep"
              >
                <Icon name="DocumentTextIcon" size={16} />
                Get your cash offer
                <span aria-hidden>→</span>
              </Link>
              <Link
                href="#process-timeline"
                className="inline-flex items-center justify-center gap-2 border border-border bg-card px-6 py-3 text-[12px] font-bold uppercase tracking-[0.18em] text-foreground transition-colors hover:border-plum hover:bg-surface"
              >
                <Icon name="ClockIcon" size={16} />
                See our process
              </Link>
            </div>

            {/* Trust row - mono accent */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-6 border-t border-border">
              <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <span className="text-plum">✓</span>
                No fees or commissions
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <span className="text-plum">✓</span>
                Close in 30 days or less
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                <span className="text-plum">✓</span>
                Licensed &amp; insured
              </div>
            </div>
          </div>

          {/* Luxury inquiry preview card */}
          <div className="relative">
            <div className="relative border border-border bg-card/70 p-8 shadow-card md:p-11">
              <span className="absolute left-3 top-3 h-7 w-7 border-l border-t border-accent"></span>
              <span className="absolute bottom-3 right-3 h-7 w-7 border-b border-r border-accent"></span>
              <div className="mb-6 text-center text-[11px] font-bold uppercase tracking-[0.34em] text-accent">
                — Private Seller Review —
              </div>
              <h2 className="mb-3 text-center font-heading text-[34px] font-normal leading-tight md:text-[42px]">
                Submit once. <span className="italic text-plum">Hear back quickly.</span>
              </h2>
              <p className="mx-auto mb-8 max-w-[360px] text-center font-heading text-base italic text-muted-foreground">
                Tell us about the property and we&apos;ll prepare a clear, written cash offer.
              </p>
              <div className="space-y-5">
                {['Property details', 'Owner timeline', 'Condition review', 'Written offer'].map(
                  (item) => (
                    <div key={item} className="flex items-center gap-4 border-b border-border pb-4">
                      <span className="text-accent">✦</span>
                      <span className="text-[13px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                        {item}
                      </span>
                    </div>
                  )
                )}
              </div>
              <Link
                href="#property-form"
                className="mt-9 flex w-full items-center justify-center bg-plum py-4 text-[12px] font-bold uppercase tracking-[0.34em] text-primary-foreground transition-colors hover:bg-plum-deep"
              >
                Start Inquiry
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
